<template>
  <a-modal
      v-model:visible="visible"
      title="工单审批"
      width="600px"
      :on-before-ok="handleOk"
      :confirm-loading="loading"
      @cancel="handleCancel"
      draggable
  >
    <div class="approval-modal-content">
      <a-form
          ref="formRef"
          :model="formData"
          :rules="rules"
          layout="vertical"
          :auto-label-width="true"
      >
        <a-form-item label="关联工单ID" field="workorderId">
          <a-input
              v-model="formData.workorderId"
              disabled
              placeholder="工单ID"
          />
        </a-form-item>

        <a-form-item label="审批状态" field="approvalStatus">
          <a-select
              v-model="formData.approvalStatus"
              :options="approvalStatusOptions"
              placeholder="请选择审批状态"
              allow-clear
          />
        </a-form-item>
        <a-form-item label="是否转常规工单" field="toRoutine" v-if="formData.approvalStatus==='3' && workeroderData?.workorderType==='5'">
          <a-switch v-model="formData.toRoutine"/>
        </a-form-item>

        <a-form-item label="审批意见" field="approvalResult" :rules="[{ required: formData.approvalStatus!=='3', message: '请输入审批意见' }]">
          <a-textarea
              v-model="formData.approvalResult"
              placeholder="请输入审批意见"
              :rows="4"
              allow-clear
              show-word-limit
              :max-length="500"
          />
        </a-form-item>
      </a-form>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import { Message } from '@arco-design/web-vue'
import { addApproval, getWorkOrder } from '@/apis/work-order-api'
import { useSysDictDataStore } from '@/store/system-data'

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  workOrderId: {
    type: [String, Number],
    default: null
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue', 'success'])

// 系统字典 store
const sysDictDataStore = useSysDictDataStore();

// 审批状态选项（排除“待审批”状态，只显示可选择的状态）
const approvalStatusOptions = computed(() => {
  return sysDictDataStore.workOrderApprovalStatusList
    .filter(item => item.dictValue !== '1') // 排除待审批状态
    .map(item => ({
      value: item.dictValue,
      label: item.dictLabel
    }));
});

// 响应式数据
const visible = ref(false)
const loading = ref(false)
const formRef = ref()
const workeroderData = ref(null)

// 表单数据
const formData = ref({
  workorderId: '',
  approvalStatus: null,
  toRoutine: false,
  approvalResult: ''
})

// 表单验证规则
const rules = ref({
  workorderId: [
    { required: true, message: '工单ID不能为空' }
  ],
  approvalStatus: [
    { required: true, message: '请选择审批状态' }
  ],
  approvalResult: [
    { minLength: 1, message: '审批意见至少1个有效字符' },
    { maxLength: 500, message: '审批意见不能超过500个字符' }
  ]
})

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal && props.workOrderId) {
    initFormData()
    fetchWorkOrderData()
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 获取工单类型
const fetchWorkOrderData = async () => {
  try {
    const response = await getWorkOrder(props.workOrderId)
    if (response.code === 0) {
      workeroderData.value = response.data
    } else {
      Message.error(response.message || '获取工单信息失败')
    }
  } catch (error) {
    Message.error('获取工单信息失败: ' + (error.message || '未知错误'))
    console.error('获取工单信息失败:', error)
  }
}

// 初始化表单数据
const initFormData = () => {
  formData.value = {
    workorderId: props.workOrderId,
    approvalStatus: null,
    approvalResult: ''
  }

  nextTick(() => {
    formRef.value?.clearValidate()
  })
}

// 处理取消
const handleCancel = () => {
  resetForm()
}

// 重置表单
const resetForm = () => {
  formData.value = {
    workorderId: '',
    approvalStatus: null,
    approvalResult: ''
  }

  nextTick(() => {
    formRef.value?.clearValidate()
  })
}

// 处理确认按钮
const handleOk = async (done) => {
  try {
    // 验证表单
    const validateResult = await formRef.value?.validate()

    // 如果验证失败，阻止提交和关闭弹框
    if (validateResult !== undefined) {
      done(false)
      return false
    }

    loading.value = true

    // 调用审批接口
    const response = await addApproval(formData.value)

    if (response.code === 0) {
      Message.success('工单审批成功')
      emit('success')
      resetForm()
      done(true)
      return true
    } else {
      Message.error(response.message || '工单审批失败')
      done(false)
      return false
    }
  } catch (error) {
    Message.error('工单审批失败')
    console.error('审批失败:', error)
    done(false)
    return false
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.approval-modal-content {
  padding: 16px 0;
}

:deep(.arco-form-item-label) {
  font-weight: 500;
  color: #1890ff;
}

:deep(.arco-input[disabled]) {
  background-color: #f5f5f5;
  color: #666;
}
</style>